import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Star, Trash2, Check } from "lucide-react";
import { useTr } from "@/i18n/translator";

const TONES = ["formal", "casual", "divertido", "profesional"];
const LANGS = [
  ["es-MX", "Español (MX)"], ["es-AR", "Español (AR)"], ["es-ES", "Español (ES)"],
  ["es-CO", "Español (CO)"], ["en-US", "English (US)"], ["pt-BR", "Português (BR)"],
];

function emptyBrand() {
  return {
    brand_name: "", tone: "casual", topics: [], banned_words: [], use_emojis: true,
    language: "es-MX", logo_url: "", primary_color: "#E53935",
    geo_enabled: false, geo_city: "", geo_country: "", geo_neighborhood: "",
    geo_radius_km: 5, geo_lat: 0, geo_lng: 0,
  };
}

export default function BrandConfig() {
  const tr = useTr();
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [topicsInput, setTopicsInput] = useState("");
  const [bannedInput, setBannedInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [limits, setLimits] = useState(null);

  const load = async () => {
    const { data } = await api.get("/brands");
    setBrands(data);
    const active = data.find((b) => b.is_active) || data[0];
    if (active) selectBrand(active);
    try {
      const { data: sub } = await api.get("/billing/subscription");
      setLimits({ plan: sub.plan, max: sub.limits?.brands ?? 1, used: sub.usage?.brands ?? data.length });
    } catch {}
  };

  const selectBrand = (b) => {
    setEditing(b);
    setTopicsInput((b.topics || []).join(", "));
    setBannedInput((b.banned_words || []).join(", "));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        brand_name: editing.brand_name,
        tone: editing.tone,
        use_emojis: editing.use_emojis,
        language: editing.language,
        logo_url: editing.logo_url || "",
        primary_color: editing.primary_color || "#E53935",
        topics: topicsInput.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10),
        banned_words: bannedInput.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (creating) {
        const { data } = await api.post("/brands", payload);
        toast.success("Marca creada");
        setCreating(false);
        await load();
        selectBrand(data);
      } else {
        await api.patch(`/brands/${editing.id}`, payload);
        toast.success("Marca actualizada");
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const activate = async (b) => {
    await api.post("/brands/activate", { brand_id: b.id });
    toast.success(`Marca activa: ${b.brand_name}`);
    load();
  };

  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar marca "${b.brand_name}"?`)) return;
    try {
      await api.delete(`/brands/${b.id}`);
      toast.success("Marca eliminada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const startCreate = () => {
    setCreating(true);
    const e = emptyBrand();
    setEditing({ ...e, id: "new", user_id: "", created_at: "", is_active: false });
    setTopicsInput("");
    setBannedInput("");
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="brand-page">
      {/* Sidebar list */}
      <aside className="lg:col-span-4">
        <div className="bg-white border border-[#0A0A0A] p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("MARCAS")}</div>
              {limits && (
                <div className={`font-mono-mark text-[9px] mt-0.5 ${limits.used >= limits.max ? "text-[#E53935]" : "text-[#5C5C5C]"}`} data-testid="brand-quota">
                  {limits.used} / {limits.max} · PLAN {limits.plan.toUpperCase()}
                </div>
              )}
            </div>
            <button
              onClick={startCreate}
              disabled={limits && limits.used >= limits.max}
              className="font-mono-mark text-[10px] bg-[#E53935] text-white px-2 py-1 border border-[#E53935] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="brand-add"
              title={limits && limits.used >= limits.max ? `Límite alcanzado (${limits.max} marcas en plan ${limits.plan.toUpperCase()})` : "Añadir nueva marca"}
            >
              <Plus className="w-3 h-3" /> {tr("NUEVA")}
            </button>
          </div>
          <ul className="space-y-2">
            {brands.map((b) => (
              <li
                key={b.id}
                className={`border p-3 cursor-pointer ${editing?.id === b.id ? "border-[#E53935] bg-[#F4F4F0]" : "border-[#0A0A0A] hover:bg-[#F4F4F0]"}`}
                onClick={() => { setCreating(false); selectBrand(b); }}
                data-testid={`brand-item-${b.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg truncate">{b.brand_name}</div>
                  {b.is_active && <Star className="w-4 h-4 fill-[#FFD600] text-[#0A0A0A]" />}
                </div>
                <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-1">{b.tone.toUpperCase()} · {b.language}</div>
                <div className="flex gap-2 mt-3">
                  {!b.is_active && (
                    <button onClick={(e) => { e.stopPropagation(); activate(b); }} className="font-mono-mark text-[9px] px-2 py-1 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid={`brand-activate-${b.id}`}>
                      {tr("ACTIVAR")}
                    </button>
                  )}
                  {brands.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); remove(b); }} className="font-mono-mark text-[9px] px-2 py-1 border border-[#E53935] text-[#E53935]" data-testid={`brand-remove-${b.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Editor */}
      <section className="lg:col-span-8">
        {!editing ? (
          <div className="font-mono-mark text-xs text-[#5C5C5C]">{tr("Selecciona una marca para editarla.")}</div>
        ) : (
          <div className="bg-white border border-[#0A0A0A] p-6 space-y-5">
            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
                {creating ? "CREAR MARCA" : `EDITANDO · ${editing.is_active ? "ACTIVA" : "INACTIVA"}`}
              </div>
              <h2 className="font-display text-3xl mt-2">{editing.brand_name || "Nueva marca"}</h2>
            </div>

            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("NOMBRE DE LA MARCA")}</span>
              <input
                value={editing.brand_name}
                onChange={(e) => setEditing({ ...editing, brand_name: e.target.value })}
                className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
                data-testid="brand-name"
              />
            </label>

            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">{tr("TONO DE VOZ")}</div>
              <div className="grid grid-cols-4 gap-2">
                {TONES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setEditing({ ...editing, tone: t })}
                    className={`px-2 py-2 font-mono-mark text-[10px] border ${editing.tone === t ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`}
                    data-testid={`brand-tone-${t}`}
                  >
                    {tr(t).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("TEMAS (MÁX 10, COMA)")}</span>
              <input value={topicsInput} onChange={(e) => setTopicsInput(e.target.value)} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="brand-topics" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PALABRAS PROHIBIDAS (COMA)")}</span>
              <input value={bannedInput} onChange={(e) => setBannedInput(e.target.value)} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="brand-banned" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("LOGO (PNG/JPG/SVG · MÁX 2MB)")}</span>
              {editing.logo_url && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={editing.logo_url} alt="logo" className="w-20 h-20 object-contain bg-[#F4F4F0] border border-[#0A0A0A]" data-testid="brand-logo-preview" />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!editing.id) { setEditing({ ...editing, logo_url: "" }); return; }
                      try {
                        await api.delete(`/brands/${editing.id}/logo`);
                        setEditing({ ...editing, logo_url: "" });
                        toast.success(tr("Logo eliminado"));
                      } catch {}
                    }}
                    className="font-mono-mark text-[10px] bg-white border border-[#E53935] text-[#E53935] px-3 py-2"
                    data-testid="brand-logo-remove"
                  >
                    {tr("ELIMINAR LOGO")}
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { toast.error(tr("Máximo 2MB")); return; }
                  if (!editing.id) {
                    // brand aún no creada: preview local como data URL
                    const reader = new FileReader();
                    reader.onload = () => setEditing({ ...editing, logo_url: reader.result });
                    reader.readAsDataURL(file);
                    return;
                  }
                  try {
                    const fd = new FormData();
                    fd.append("file", file);
                    const { data } = await api.post(`/brands/${editing.id}/logo`, fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    setEditing({ ...editing, logo_url: data.logo_url });
                    toast.success(tr(`Logo subido · ${data.size_kb}KB`));
                  } catch (err) {
                    toast.error(err.response?.data?.detail || tr("Error al subir"));
                  }
                }}
                className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm file:mr-3 file:px-3 file:py-1 file:border file:border-[#0A0A0A] file:bg-[#FFD600] file:font-mono-mark file:text-[10px]"
                data-testid="brand-logo-file"
              />
            </label>

            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("COLOR PRIMARIO · WHITE-LABEL")}</span>
              <div className="flex gap-3 items-center mt-2">
                <input
                  type="color"
                  value={editing.primary_color || "#E53935"}
                  onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })}
                  className="w-16 h-12 border border-[#0A0A0A] cursor-pointer"
                  data-testid="brand-color-picker"
                />
                <input
                  type="text"
                  value={editing.primary_color || "#E53935"}
                  onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })}
                  placeholder="#E53935"
                  className="flex-1 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm uppercase"
                  data-testid="brand-color-input"
                />
                <div
                  className="px-4 py-3 text-white font-mono-mark text-[10px]"
                  style={{ backgroundColor: editing.primary_color || "#E53935" }}
                  data-testid="brand-color-preview"
                >
                  PREVIEW
                </div>
              </div>
              <div className="mt-1 font-mono-mark text-[9px] text-[#5C5C5C]">
                {tr("SE APLICA A BOTONES, BADGES Y ACENTOS DEL DASHBOARD CUANDO ESTA MARCA ESTÁ ACTIVA.")}
              </div>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("IDIOMA")}</span>
                <select value={editing.language} onChange={(e) => setEditing({ ...editing, language: e.target.value })} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="brand-language">
                  {LANGS.map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}
                </select>
              </label>
              <div>
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("EMOJIS")}</span>
                <div className="flex gap-2 mt-2">
                  {[true, false].map((v) => (
                    <button key={String(v)} onClick={() => setEditing({ ...editing, use_emojis: v })}
                      className={`flex-1 py-3 font-mono-mark text-[10px] border ${editing.use_emojis === v ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white border-[#0A0A0A]"}`}
                      data-testid={`brand-emojis-${v}`}>
                      {v ? tr("SÍ") : tr("NO")}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* GEO · LOCAL TARGETING */}
            <div className="pt-6 border-t border-[#0A0A0A]">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("GEO · ALCANCE LOCAL")}</span>
                  <p className="font-mono-data text-xs text-[#5C5C5C] mt-1 max-w-md">
                    {tr("Si tu marca es local (cafetería, gym, barbería, tienda), activa geo-targeting. La IA incluirá ubicación y hashtags locales en cada post para atraer a gente cerca de ti.")}
                  </p>
                </div>
                <button
                  onClick={() => setEditing({ ...editing, geo_enabled: !editing.geo_enabled })}
                  className={`px-5 py-3 font-mono-mark text-[10px] border ${editing.geo_enabled ? "bg-[#FFD600] text-[#0A0A0A] border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`}
                  data-testid="brand-geo-toggle"
                >
                  {editing.geo_enabled ? tr("ACTIVO") : tr("INACTIVO")}
                </button>
              </div>

              {editing.geo_enabled && (
                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PAÍS (ISO 2)")}</span>
                    <input type="text" maxLength={2} placeholder="MX, CO, AR…" value={editing.geo_country}
                      onChange={(e) => setEditing({ ...editing, geo_country: e.target.value.toUpperCase() })}
                      className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm uppercase"
                      data-testid="brand-geo-country" />
                  </label>
                  <label className="block">
                    <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CIUDAD")}</span>
                    <input type="text" placeholder="Ciudad de México" value={editing.geo_city}
                      onChange={(e) => setEditing({ ...editing, geo_city: e.target.value })}
                      className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                      data-testid="brand-geo-city" />
                  </label>
                  <label className="block">
                    <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("COLONIA / BARRIO")}</span>
                    <input type="text" placeholder="Condesa, Palermo, Chapinero…" value={editing.geo_neighborhood}
                      onChange={(e) => setEditing({ ...editing, geo_neighborhood: e.target.value })}
                      className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                      data-testid="brand-geo-neighborhood" />
                  </label>
                  <label className="block">
                    <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("RADIO DE ALCANCE (KM)")}</span>
                    <input type="number" min={1} max={50} value={editing.geo_radius_km}
                      onChange={(e) => setEditing({ ...editing, geo_radius_km: parseInt(e.target.value || "5", 10) })}
                      className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                      data-testid="brand-geo-radius" />
                  </label>
                  <div className="md:col-span-2 bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-xs" data-testid="brand-geo-preview">
                    <b>{tr("Vista previa:")}</b>{" "}
                    {editing.geo_neighborhood && `${editing.geo_neighborhood}, `}
                    {editing.geo_city || tr("(ciudad)")}, {editing.geo_country || "??"} · {editing.geo_radius_km} km
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-[#0A0A0A] flex gap-3">
              <button onClick={save} disabled={saving} className="bg-[#0A0A0A] text-white px-6 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] disabled:opacity-50 flex items-center gap-2" data-testid="brand-save">
                <Check className="w-4 h-4" />
                {saving ? tr("GUARDANDO…") : creating ? tr("CREAR MARCA") : tr("GUARDAR")}
              </button>
              {creating && (
                <button onClick={() => { setCreating(false); load(); }} className="bg-white text-[#0A0A0A] px-6 py-3 font-mono-mark text-xs border border-[#0A0A0A]">
                  {tr("CANCELAR")}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
